import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Circle, ChevronUp, ChevronDown, X, Sparkles } from 'lucide-react';
import { useOnboarding } from '../../context/OnboardingContext';

// ─── Step definitions ──────────────────────────────────────────────────────────

interface ChecklistStep {
  key: string;
  labelKey: string;
  labelDefault: string;
  route: string;
}

const CREATOR_STEPS: ChecklistStep[] = [
  { key: 'createHousehold',        labelKey: 'onboarding.checklist.step_create_household',        labelDefault: 'Create your household',      route: '/dashboard' },
  { key: 'inviteMember',           labelKey: 'onboarding.checklist.step_invite_member',           labelDefault: 'Invite a member',            route: '/settings' },
  { key: 'addThreeTasks',          labelKey: 'onboarding.checklist.step_add_three_tasks',         labelDefault: 'Add 3 tasks',                route: '/tasks' },
  { key: 'addCalendarEvent',       labelKey: 'onboarding.checklist.step_add_calendar_event',      labelDefault: 'Add a calendar event',       route: '/calendar' },
  { key: 'setupGroceryCategories', labelKey: 'onboarding.checklist.step_setup_grocery_categories',labelDefault: 'Set up grocery categories',  route: '/groceries' },
  { key: 'addMaintenanceItem',     labelKey: 'onboarding.checklist.step_add_maintenance_item',    labelDefault: 'Add a maintenance item',     route: '/maintenance' },
];

const JOINER_STEPS: ChecklistStep[] = [
  { key: 'joinHousehold',   labelKey: 'onboarding.checklist.step_join_household',    labelDefault: 'Join your household',    route: '/dashboard' },
  { key: 'createFirstTask', labelKey: 'onboarding.checklist.step_create_first_task', labelDefault: 'Create your first task', route: '/tasks' },
];

// ─── Component ────────────────────────────────────────────────────────────────

export const GettingStartedWidget: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { steps, isComplete, loading, dismissPermanently } = useOnboarding();
  const [expanded, setExpanded] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  if (loading || isComplete) return null;

  // Default to creator steps while userType is being determined (avoids blank widget)
  const checklistSteps = steps.userType === 'joiner' ? JOINER_STEPS : CREATOR_STEPS;
  const completedCount = checklistSteps.filter((s) => steps[s.key as keyof typeof steps]).length;
  const totalCount = checklistSteps.length;
  const progressPct = Math.round((completedCount / totalCount) * 100);

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      await dismissPermanently();
    } finally {
      setDismissing(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-[90] flex flex-col items-end gap-2">
      {/* Expanded panel */}
      {expanded && (
        <div className="w-72 bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary-400" />
              <span className="text-sm font-bold text-white">
                {t('onboarding.checklist.title', 'Getting Started')}
              </span>
            </div>
            <button
              onClick={handleDismiss}
              disabled={dismissing}
              className="text-slate-500 hover:text-slate-300 transition-colors"
              aria-label={t('onboarding.checklist.dismiss', 'Dismiss permanently')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Progress bar */}
          <div className="px-4 pt-3 pb-1">
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-2xs text-slate-400 font-semibold uppercase tracking-wider">
                {t('onboarding.checklist.progress', '{{done}} of {{total}} complete', {
                  done: completedCount,
                  total: totalCount,
                })}
              </span>
              <span className="text-2xs text-primary-400 font-bold">{progressPct}%</span>
            </div>
            <div className="w-full bg-slate-800 rounded-full h-1.5">
              <div
                className="bg-primary-500 h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>

          {/* Step list */}
          <ul className="px-3 py-2 space-y-0.5">
            {checklistSteps.map((step) => {
              const done = Boolean(steps[step.key as keyof typeof steps]);
              return (
                <li key={step.key}>
                  <button
                    onClick={() => !done && navigate(step.route)}
                    disabled={done}
                    className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-xl text-left transition-colors ${
                      done
                        ? 'opacity-50 cursor-default'
                        : 'hover:bg-slate-800 cursor-pointer'
                    }`}
                  >
                    {done ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                    ) : (
                      <Circle className="w-4 h-4 text-slate-600 shrink-0" />
                    )}
                    <span className={`text-sm ${done ? 'line-through text-slate-500' : 'text-slate-300'}`}>
                      {t(step.labelKey, step.labelDefault)}
                    </span>
                    {!done && (
                      <span className="ml-auto text-2xs text-primary-400 font-semibold shrink-0">
                        {t('onboarding.checklist.go', 'Go →')}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Dismiss link */}
          <div className="px-4 py-3 border-t border-slate-800">
            <button
              onClick={handleDismiss}
              disabled={dismissing}
              className="text-2xs text-slate-500 hover:text-slate-400 transition-colors"
            >
              {t('onboarding.checklist.dismiss_link', "I'll figure it out — dismiss")}
            </button>
          </div>
        </div>
      )}

      {/* Pill toggle button */}
      <button
        onClick={() => setExpanded((prev) => !prev)}
        className="flex items-center gap-2 bg-primary-600 hover:bg-primary-500 text-white px-4 py-2.5 rounded-full shadow-lg shadow-primary-900/40 font-semibold text-sm transition-all"
        aria-expanded={expanded}
        aria-label={t('onboarding.checklist.toggle', 'Toggle getting started checklist')}
      >
        <Sparkles className="w-4 h-4" />
        <span>
          {t('onboarding.checklist.pill_label', 'Getting Started')}
          {' · '}
          {completedCount}/{totalCount}
        </span>
        {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
};
